import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './health-safety-page.style.js';
import { SafetyReportType, SafetyReportStatus, SafetyActionStatus, SubscriptionPlanCat } from '@cmms/models';
import { getSafetyReportsByCompany, getActionsByCompany, getUsersByCompany, getLocationsByCompany } from '@cmms/api';
import { db } from '../../services/auth.service.js';
import '../../common/upgrade-placeholder/upgrade-placeholder.js';
import '../../common/upgrade-modal/upgrade-modal.js';


@customElement('health-safety-page')
export class HealthSafetyPage extends LitElement {
  @property({ type: String })
  companyId: string = '';

  @property({ type: Number })
  subscriptionPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;

  static styles = styles;

  @state() private safetyReports: any[] = [];
  @state() private showUpgradeModal = false;
  @state() private upgradeContext = { action: '', feature: '' };
  @state() private safetyActions: any[] = [];
  @state() private users: any[] = [];
  @state() private locations: any[] = [];
  @state() private loading = false;
  @state() private error = '';
  @state() private activeTab = 'reports';
  @state() private showReportModal = false;
  @state() private showActionModal = false;
  @state() private selectedReport: any = null;
  @state() private selectedAction: any = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  private hasSafetyAccess(): boolean {
    // Only Enterprise (3) has access, Starter (1) and Professional (2) do not
    return this.subscriptionPlan === SubscriptionPlanCat.ENTERPRISE;
  }

  private handleUpgradeRequest(event: CustomEvent) {
    this.upgradeContext = {
      action: event.detail.action || 'Access',
      feature: 'Health & Safety Management'
    };
    this.showUpgradeModal = true;
  }

  private async loadData() {
    this.loading = true;
    this.error = '';

    try {
      // Use actual API calls
      const [reportsResponse, actionsResponse, usersResponse, locationsResponse] = await Promise.all([
        getSafetyReportsByCompany(db, this.companyId),
        getActionsByCompany(db, this.companyId),
        getUsersByCompany(db, this.companyId),
        getLocationsByCompany(db, this.companyId)
      ]);
      
      // Batch all state updates to prevent multiple re-renders
      const newSafetyReports = reportsResponse.data ? 
        reportsResponse.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        })) : [];
      
      const newSafetyActions = actionsResponse.data ? 
        actionsResponse.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        })) : [];
      
      const newUsers = usersResponse.data ? 
        usersResponse.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        })) : [];
      
      const newLocations = locationsResponse.data ? 
        locationsResponse.data.map(item => ({
          id: item.id,
          ...(item.model as any).toJSON()
        })) : [];
      
      // Update all state at once
      this.safetyReports = newSafetyReports;
      this.safetyActions = newSafetyActions;
      this.users = newUsers;
      this.locations = newLocations;
      
    } catch (err) {
      this.error = 'Failed to load safety data';
    } finally {
      this.loading = false;
    }
  }



  private getStatusBadgeVariant(status: string) {
    if (!status) return 'neutral';
    
    switch (status) {
      case SafetyReportStatus.OPEN:
        return 'warning';
      case SafetyReportStatus.ASSIGNED:
        return 'primary';
      case SafetyReportStatus.INVESTIGATING:
        return 'danger';
      case SafetyReportStatus.RESOLVED:
        return 'success';
      case SafetyActionStatus.TODO:
        return 'neutral';
      case SafetyActionStatus.IN_PROGRESS:
        return 'primary';
      case SafetyActionStatus.COMPLETED:
        return 'success';
      default:
        return 'neutral';
    }
  }

  private getLocationName(locationId: string): string {
    if (!locationId) return '';
    const location = this.locations.find(loc => loc.id === locationId);
    return location ? location.name : '';
  }

  private getTypeBadgeVariant(type: string) {
    if (!type) return 'neutral';
    
    switch (type) {
      case SafetyReportType.SAFETY_OBSERVATION:
        return 'warning';
      case SafetyReportType.NEAR_MISS:
        return 'primary';
      case SafetyReportType.INCIDENT:
        return 'danger';
      default:
        return 'neutral';
    }
  }

  private getDueDateClass(dueDate: number) {
    if (!dueDate) return '';
    
    const now = Date.now();
    const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= 3) return 'due-soon';
    return '';
  }

  private formatDate(timestamp: number) {
    if (!timestamp) return 'Unknown Date';
    return new Date(timestamp).toLocaleDateString();
  }

  private getReportsTableConfig(data: any[]) {
    return {
      data: data,
      headerActions: [
        {
          key: 'new-report',
          label: 'New Report',
          variant: 'neutral',
          icon: 'plus',
          onClick: () => this.createNewReport()
        },
        {
          key: 'new-action',
          label: 'New Action',
          variant: 'primary',
          icon: 'plus',
          onClick: () => this.createNewAction()
        }
      ],
      filters: [
        {
          key: 'status',
          type: 'select',
          label: 'Status',
          placeholder: 'All Statuses',
          options: [
            { value: 'open', label: 'Open' },
            { value: 'assigned', label: 'Assigned' },
            { value: 'investigating', label: 'Investigating' },
            { value: 'resolved', label: 'Resolved' }
          ]
        },
        {
          key: 'report_type',
          type: 'select',
          label: 'Type',
          placeholder: 'All Types',
          options: [
            { value: 'safety_observation', label: 'Safety Observation' },
            { value: 'incident_report', label: 'Incident Report' },
            { value: 'near_miss', label: 'Near Miss' }
          ]
        }
      ],
      searchPlaceholder: 'Search reports...',
      columns: [
        {
          key: 'title',
          label: 'Title',
          type: 'text',
          sortable: true,
          renderer: (value: string, row: any) => html`
            <div style="font-weight: 500; color: var(--sl-color-neutral-700);">
              ${value}
            </div>
          `
        },
        {
          key: 'report_type',
          label: 'Type',
          type: 'badge',
          sortable: true,
          badgeVariant: (value: string) => this.getTypeBadgeVariant(value),
          badgeLabel: (value: string) => value ? value.replace('_', ' ').toUpperCase() : 'UNKNOWN'
        },
        {
          key: 'status',
          label: 'Status',
          type: 'badge',
          sortable: true,
          badgeVariant: (value: string) => this.getStatusBadgeVariant(value),
          badgeLabel: (value: string) => value ? value.toUpperCase() : 'UNKNOWN'
        },
        {
          key: 'location_id',
          label: 'Location',
          type: 'text',
          sortable: true,
          formatter: (value: string) => this.getLocationName(value) || 'Unknown Location'
        },
        {
          key: 'date_reported',
          label: 'Date Reported',
          type: 'date',
          sortable: true,
          formatter: (value: number) => this.formatDate(value)
        },
        {
          key: 'actions',
          label: 'Actions',
          type: 'custom',
          sortable: false,
          renderer: (value: any, row: any) => html`
            <div class="actions">
              <sl-button size="small" variant="text" @click="${() => this.viewReport(row)}">
                <sl-icon name="eye"></sl-icon>
              </sl-button>
              <sl-button size="small" variant="text" @click="${() => this.editReport(row)}">
                <sl-icon name="pencil"></sl-icon>
              </sl-button>
            </div>
          `
        }
      ]
    };
  }

  private getActionsTableConfig(data: any[]) {
    return {
      data: data,
      headerActions: [
        {
          key: 'new-report',
          label: 'New Report',
          variant: 'neutral',
          icon: 'plus',
          onClick: () => this.createNewReport()
        },
        {
          key: 'new-action',
          label: 'New Action',
          variant: 'primary',
          icon: 'plus',
          onClick: () => this.createNewAction()
        }
      ],
      filters: [
        {
          key: 'status',
          type: 'select',
          label: 'Status',
          placeholder: 'All Statuses',
          options: [
            { value: 'todo', label: 'To Do' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'completed', label: 'Completed' },
            { value: 'overdue', label: 'Overdue' }
          ]
        }
      ],
      searchPlaceholder: 'Search actions...',
      columns: [
        {
          key: 'action_title',
          label: 'Action Title',
          type: 'text',
          sortable: true,
          formatter: (value: string) => value
        },
        {
          key: 'status',
          label: 'Status',
          type: 'badge',
          sortable: true,
          badgeVariant: (value: string) => this.getStatusBadgeVariant(value),
          badgeLabel: (value: string) => value ? value.replace('_', ' ').toUpperCase() : 'UNKNOWN'
        },
        {
          key: 'assigned_to_user_id',
          label: 'Assigned To',
          type: 'text',
          sortable: true,
          formatter: (value: string, row: any) => {
            if (!value) return 'Unassigned';
            // Try to find user name from the users data
            const user = this.users?.find(u => u.id === value);
            return user ? (user.name || user.email || `User ${value}`) : `User ${value}`;
          }
        },
        {
          key: 'due_date',
          label: 'Due Date',
          type: 'date',
          sortable: true,
          formatter: (value: number) => this.formatDate(value)
        },
        {
          key: 'related_report_id',
          label: 'Related Report',
          type: 'text',
          sortable: true,
          formatter: (value: string, row: any) => {
            if (!value) return 'None';
            // Try to find report title from the safety reports data
            const report = this.safetyReports?.find(r => r.id === value);
            return report ? (report.title || report.report_title || report.incident_title || `Report ${value}`) : `Report ${value}`;
          }
        },
        {
          key: 'actions',
          label: 'Actions',
          type: 'custom',
          sortable: false,
          renderer: (value: any, row: any) => html`
            <div class="actions">
              <sl-button size="small" variant="text" @click="${() => this.viewAction(row)}">
                <sl-icon name="eye"></sl-icon>
              </sl-button>
              <sl-button size="small" variant="text" @click="${() => this.editAction(row)}">
                <sl-icon name="pencil"></sl-icon>
              </sl-button>
            </div>
          `
        }
      ]
    };
  }

  private viewReport(report: any) {
    this.selectedReport = report;
    // TODO: Implement report viewer modal
  }

  private editReport(report: any) {
    this.selectedReport = report;
    this.showReportModal = true;
  }

  private createNewReport() {
    this.selectedReport = null;
    this.showReportModal = true;
  }

  private handleReportModalClosed = () => {
    this.showReportModal = false;
    this.selectedReport = null;
  };

  private handleReportSaved = () => {
    this.loadData();
    this.handleReportModalClosed();
  };

  private viewAction(action: any) {
    const viewer = document.createElement('safety-action-viewer') as any;
    viewer.action = action;
    viewer.users = this.users;
    viewer.safetyReports = this.safetyReports;
    document.body.appendChild(viewer);
    
    // Show the dialog
    requestAnimationFrame(() => {
      const dialog = viewer.shadowRoot?.querySelector('sl-dialog');
      if (dialog) {
        dialog.show();
      }
    });
  }

  private editAction(action: any) {
    this.selectedAction = action;
    this.showActionModal = true;
  }

  private createNewAction() {
    this.selectedAction = null;
    this.showActionModal = true;
  }

  private handleActionModalClosed = () => {
    this.showActionModal = false;
    this.selectedAction = null;
  };

  private handleActionSaved = () => {
    this.loadData();
    this.handleActionModalClosed();
  };


  private onTabChange(event: any) {
    this.activeTab = event.target.panel;
  }

  render() {
    // Show upgrade placeholder for Basic plan users
    if (!this.hasSafetyAccess()) {
      return html`
        <div class="health-safety-page">
          <upgrade-placeholder
            featureName="Health & Safety Management"
            description="Maintain a safe workplace with comprehensive incident reporting, safety actions tracking, and compliance management tools."
            icon="shield-check"
            .currentPlan=${this.subscriptionPlan}
            .benefits=${[
              'Incident and near-miss reporting system',
              'Safety action tracking and follow-up',
              'Compliance documentation and auditing',
              'Risk assessment and hazard identification',
              'Safety training records management',
              'Automated safety notifications and alerts'
            ]}
            @upgrade-requested=${this.handleUpgradeRequest}
          ></upgrade-placeholder>

          <!-- Upgrade Modal -->
          <upgrade-modal
            ?open=${this.showUpgradeModal}
            featureName=${this.upgradeContext.feature}
            actionName=${this.upgradeContext.action}
            @upgrade-confirmed=${this.handleUpgradeRequest}
            @sl-request-close=${() => this.showUpgradeModal = false}
          ></upgrade-modal>
        </div>
      `;
    }

    return html`


      <sl-tab-group @sl-tab-change="${this.onTabChange}">
        <sl-tab slot="nav" panel="reports">Safety Reports</sl-tab>
        <sl-tab slot="nav" panel="actions">Safety Actions</sl-tab>

        <sl-tab-panel name="reports" class="tab-content">

          ${this.error ? html`
            <sl-alert variant="danger" open>
              <sl-icon name="exclamation-triangle" slot="icon"></sl-icon>
              ${this.error}
            </sl-alert>
          ` : ''}

          ${this.loading ? html`
            <div class="loading-container">
              <sl-spinner></sl-spinner>
            </div>
          ` : this.safetyReports.length === 0 ? html`
            <div class="empty-state">
              <sl-icon name="shield-exclamation"></sl-icon>
              <h3>No safety reports found</h3>
              <p>Create your first safety report to get started.</p>
            </div>
          ` : html`
            <data-table
              .config=${this.getReportsTableConfig(this.safetyReports) as any}
            ></data-table>
          `}
        </sl-tab-panel>

        <sl-tab-panel name="actions" class="tab-content">

          ${this.loading ? html`
            <div class="loading-container">
              <sl-spinner></sl-spinner>
            </div>
          ` : this.safetyActions.length === 0 ? html`
            <div class="empty-state">
              <sl-icon name="list-check"></sl-icon>
              <h3>No safety actions found</h3>
              <p>Create your first safety action to get started.</p>
            </div>
          ` : html`
            <data-table
              .config=${this.getActionsTableConfig(this.safetyActions) as any}
            ></data-table>
          `}
        </sl-tab-panel>
      </sl-tab-group>

      <!-- Safety Report Modal -->
      <safety-report-modal
        .open=${this.showReportModal}
        .report=${this.selectedReport}
        .companyId=${this.companyId}
        @modal-closed=${this.handleReportModalClosed}
        @safety-report-saved=${this.handleReportSaved}
      ></safety-report-modal>

      <!-- Safety Action Modal -->
      <safety-action-modal
        .open=${this.showActionModal}
        .action=${this.selectedAction}
        .companyId=${this.companyId}
        @modal-closed=${this.handleActionModalClosed}
        @safety-action-saved=${this.handleActionSaved}
      ></safety-action-modal>
    `;
  }
}
